import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, isAdmin } from '../lib/supabase'
import { writeAuditLog } from '../lib/auditLog'
import type { Resource, ResourceInsert, ResourceUpdate } from '../types/database'
import { Category } from '../types'

export function useResources(category?: string, config?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['resources', category],
    queryFn: async () => {
      let query = supabase
        .from('resources')
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })

      if (category && category !== Category.ALL) {
        query = query.eq('category', category)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Resource[]
    },
    enabled: config?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  })
}

export function useResource(id: string) {
  return useQuery({
    queryKey: ['resource', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data as Resource
    },
    enabled: !!id,
  })
}

export function useCreateResource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (resource: ResourceInsert) => {
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id || null
      const visibility = resource.visibility || 'public'

      let publishStatus: string | undefined
      if (visibility === 'public' && userId) {
        const adminUser = await isAdmin(userId)
        if (adminUser) {
          publishStatus = 'published'
        }
      }

      const resourceWithOwner = {
        ...resource,
        created_by: userId,
        owner_id: userId,
        visibility,
        ...(publishStatus && { publish_status: publishStatus }),
      }

      const { data, error } = await supabase
        .from('resources')
        .insert(resourceWithOwner as Record<string, unknown>)
        .select()
        .single()

      if (error) throw error

      await writeAuditLog({
        action: 'resource.create',
        resourceType: 'resource',
        resourceId: (data as Resource).id,
        oldValue: null,
        newValue: {
          category: (data as Resource).category,
          category_l1: (data as Resource).category_l1,
          category_l2: (data as Resource).category_l2,
        },
      })

      return data as Resource
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['teamResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}

export function useMyResources(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['myResources', userId],
    queryFn: async () => {
      if (!userId) return []

      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('owner_id', userId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as Resource[]
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdateResource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: ResourceUpdate }) => {
      const { data: before, error: beforeError } = await supabase
        .from('resources')
        .select('category, category_l1, category_l2')
        .eq('id', id)
        .single()

      if (beforeError) throw beforeError

      const { data, error } = await supabase
        .from('resources')
        .update({ ...updates, updated_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      await writeAuditLog({
        action: 'resource.update',
        resourceType: 'resource',
        resourceId: id,
        oldValue: {
          category: (before as Resource).category,
          category_l1: (before as Resource).category_l1,
          category_l2: (before as Resource).category_l2,
        },
        newValue: {
          category: (data as Resource).category,
          category_l1: (data as Resource).category_l1,
          category_l2: (data as Resource).category_l2,
          category_source: (data as Resource).category_source,
        },
      })

      return data as Resource
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['teamResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}

export function useDeleteResource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('resources')
        .update({ is_deleted: true, updated_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('id', id)
        .eq('owner_id', user.id)
        .eq('is_deleted', false)
        .select('*')

      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error('Resource not found or not authorized')
      }

      await writeAuditLog({
        action: 'resource.delete',
        resourceType: 'resource',
        resourceId: id,
        oldValue: { is_deleted: false },
        newValue: { is_deleted: true },
      })

      return (data as Resource[])[0]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['teamResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}

export function useAdminDeleteResource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      const adminUser = await isAdmin(user.id)
      if (!adminUser) throw new Error('Not authorized')

      const { data, error } = await supabase
        .from('resources')
        .update({ is_deleted: true, updated_at: new Date().toISOString() } as Record<string, unknown>)
        .eq('id', id)
        .eq('is_deleted', false)
        .select('*')

      if (error) throw error
      if (!data || data.length === 0) {
        throw new Error('Resource not found or not authorized')
      }

      await writeAuditLog({
        action: 'resource.delete',
        resourceType: 'resource',
        resourceId: id,
        oldValue: { is_deleted: false },
        newValue: { is_deleted: true },
      })

      return (data as Resource[])[0]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['teamResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}

export function useTeamResources(teamId: string | null | undefined, category?: string) {
  return useQuery({
    queryKey: ['teamResources', teamId, category],
    queryFn: async () => {
      if (!teamId) return []

      let query = supabase
        .from('resources')
        .select('*')
        .eq('team_id', teamId)
        .eq('visibility', 'team')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })

      if (category && category !== Category.ALL) {
        query = query.eq('category', category)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Resource[]
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSubmitForPublish() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id

      let publishStatus = 'pending_review'
      if (userId) {
        const adminUser = await isAdmin(userId)
        if (adminUser) {
          publishStatus = 'published'
        }
      }

      const { data, error } = await supabase
        .from('resources')
        .update({
          publish_status: publishStatus,
          submitted_at: new Date().toISOString(),
          review_note: null,
          ...(publishStatus === 'published' && {
            reviewed_by: userId,
            reviewed_at: new Date().toISOString(),
          }),
        } as Record<string, unknown>)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Resource
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['pendingResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}

export function usePendingResources() {
  return useQuery({
    queryKey: ['pendingResources'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('publish_status', 'pending_review')
        .eq('is_deleted', false)
        .order('submitted_at', { ascending: true })

      if (error) throw error
      return data as Resource[]
    },
    staleTime: 30 * 1000,
  })
}

export function useReviewResource() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      action,
      reviewNote,
      featured,
    }: {
      id: string
      action: 'approve' | 'reject'
      reviewNote?: string
      featured?: boolean
    }) => {
      const { data: { user } } = await supabase.auth.getUser()

      const updates: Record<string, unknown> = {
        publish_status: action === 'approve' ? 'published' : 'rejected',
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
        review_note: reviewNote || null,
      }

      if (action === 'approve' && typeof featured === 'boolean') {
        updates.featured = featured
      }

      const { data, error } = await supabase
        .from('resources')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      const auditNewValue: Record<string, unknown> = { publish_status: updates.publish_status, review_note: reviewNote }
      if (action === 'approve' && typeof featured === 'boolean') {
        auditNewValue.featured = featured
      }

      await writeAuditLog({
        action: 'resource.review',
        resourceType: 'resource',
        resourceId: id,
        oldValue: { publish_status: 'pending_review' },
        newValue: auditNewValue,
      })

      return data as Resource
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] })
      queryClient.invalidateQueries({ queryKey: ['myResources'] })
      queryClient.invalidateQueries({ queryKey: ['pendingResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResources'] })
      queryClient.invalidateQueries({ queryKey: ['accessibleResourceCounts'] })
    },
  })
}
